import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Anchor, ButtonExtendedProps, Form, ResponsiveContext } from 'grommet';
import { CircleInformation, Validate } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, ApiErrorItem, ApiRequestOptions, ApiResult, ClientService, FacilityService } from '/src/api';
import {
  AddressAutocompleteInput,
  AddressAutocompletePlaceResult,
  Box,
  BreadcrumbNav,
  ContactsList,
  DeleteFacilityModal,
  EquipmentList,
  FormCard,
  FormCardSection,
  FormPage,
  InfoBox,
  Input,
  Line,
  RetireFacilityModal,
  Text,
  UtilitySelect,
} from '/src/components';
import { config } from '/src/config';
import { useClientStore, useFacilityStore, useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import { Client, Facility, RoleTypes, TClientId, TFacilityId, User } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { FacilityDataForm, FacilityDataFormValue, FormMetadata, ReportingPeriodType } from '/src/lib/types';
import { PageNotFound } from '/src/pages';
import { facilityIsRetired, getFacilityLabel, getFormErrors, getPageTitle, getQuarterFromDate } from '/src/utils';
import { facilityDetailsTour } from '/src/utils/productTours';

export const FacilityDetailsPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const facilityStore = useFacilityStore();
  const clientStore = useClientStore();
  const screenSize = useContext(ResponsiveContext);
  const isMobile = screenSize === 'medium' || screenSize === 'small';
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /* Query Params */
  const params = useParams();
  const clientId = parseInt(params.client_id ?? '');
  const facilityId = parseInt(params.facility_id ?? '');

  /** Refs **/
  const defaultFormData = useRef<FacilityDataForm>({
    name: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_region_id: null,
    address_post_code: '',
    utility_id: null,
    program_id: null,
    last_active_reporting_quarter: null,
    comments: '',
  });

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [facility, setFacility] = useState<Facility | undefined>();
  const [facilityClient, setFacilityClient] = useState<Client | undefined>();
  const [facilityUsers, setFacilityUsers] = useState<User[]>([]);
  const [isRetired, setIsRetired] = useState(false);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [menuButtonItems, setMenuButtonItems] = useState<ButtonExtendedProps[] | ButtonExtendedProps[][]>([]);
  const [isInvalidParams, setIsInvalidParams] = useState(false);

  const [formValues, setFormValues] = useState<FacilityDataForm>(defaultFormData.current);
  const [originalFormValues, setOriginalFormValues] = useState<FacilityDataForm>(defaultFormData.current);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  /*** Computed **/
  const isCanada = useMemo(() => facility?.reporting_period_type_id === ReportingPeriodType.Yearly, [facility]);
  const isFinalized = useMemo(() => facility?.is_finalized, [facility]);

  const firstReportingPeriod = useMemo(
    () =>
      isCanada
        ? facility?.first_active_reporting_quarter?.slice(0, 4)
        : getQuarterFromDate(dayjs(facility?.first_active_reporting_quarter).toDate()),
    [isCanada, facility]
  );

  const lastReportingPeriod = useMemo(
    () =>
      facility?.last_active_reporting_quarter
        ? isCanada
          ? facility.last_active_reporting_quarter?.slice(0, 4)
          : getQuarterFromDate(dayjs(facility.last_active_reporting_quarter).toDate())
        : undefined,
    [facility, isCanada]
  );

  const titleBar = useMemo(() => {
    const badges: ReactNode[] = [];
    if (lastReportingPeriod) {
      badges.push(
        <InfoBox key="retired" pad={{ horizontal: '0.5rem' }} gap="xsmall">
          <Text size="0.95rem" weight={600} toUpperCase>
            {`${t('common:retired')}: ${lastReportingPeriod}`}
          </Text>
        </InfoBox>
      );
    }
    if (isFinalized) {
      badges.push(
        <InfoBox key="finalized" pad={{ horizontal: '0.5rem' }} gap="xsmall">
          <Validate size="16px" />
          <Text size="0.95rem" weight={600} toUpperCase>
            {t('common:finalized')}
          </Text>
        </InfoBox>
      );
    }
    if (facilityClient?.is_demo) {
      badges.push(
        <InfoBox
          key="demo"
          pad={{ horizontal: '0.5rem' }}
          gap="xsmall"
          background={{ color: 'status-critical', opacity: 'medium' }}
          border={{ color: 'status-critical' }}
        >
          <Text size="0.95rem" weight={600} toUpperCase>
            Demo Client
          </Text>
        </InfoBox>
      );
    }

    return (
      <Box gap="small" direction="row">
        {badges}
      </Box>
    );
  }, [isFinalized, lastReportingPeriod, t, facilityClient?.is_demo]);

  /** Methods **/
  const updateFormValue = (key: keyof FacilityDataForm, value: FacilityDataFormValue) => {
    setFormValues((prev) => {
      const newFormValues = {
        ...prev,
        [key]: value,
      };

      const changesExist = Object.keys(newFormValues).some(
        (k) =>
          JSON.stringify(newFormValues[k as keyof FacilityDataForm]) !==
          JSON.stringify(originalFormValues[k as keyof FacilityDataForm])
      );

      setHasChanges(changesExist);

      return newFormValues;
    });

    if (formErrors[key]) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  const handlePlaceSelected = (place: AddressAutocompletePlaceResult) => {
    if (place.line2) updateFormValue('address_line2', place.line2);
    if (place.city) updateFormValue('address_city', place.city);
    if (place.postCode) updateFormValue('address_post_code', place.postCode);
  };

  const fetchPageData = async (facilityId: TFacilityId, clientId: TClientId) => {
    try {
      const [facility, client] = await Promise.all([
        facilityStore.fetchFacility(facilityId),
        userStore.isClientUser || userStore.isFacilityUser
          ? userStore.user?.clients.find((client) => client.id === clientId)
          : ClientService.get({ id: clientId }),
      ]);

      if (!facility) throw new ApiError({} as ApiRequestOptions, {} as ApiResult, 'Facility not found');

      if (facility.client_id !== clientId) {
        facilityStore.setFacility(undefined);
        setIsInvalidParams(true);
        return;
      }

      document.title = getPageTitle(config.client.basePageTitle, getFacilityLabel(facility));

      setFacility(facility);
      setFacilityClient(client);
      setFacilityUsers(
        facility.users
          .filter((u) => u.is_active && (u.role.id === RoleTypes.ClientAdmin || u.role.id === RoleTypes.FacilityAdmin))
          .sort((a, b) => b.role_id - a.role_id)
      );

      const isRetired = facilityIsRetired(facility);
      setIsRetired(isRetired);

      const menuItems: ButtonExtendedProps[] = [];
      if (!isRetired && userStore.isFuseAdmin) {
        menuItems.push({ label: t('facility:retireFacility'), onClick: () => setShowRetireModal(true) });
        if (!facility.is_finalized) {
          menuItems.push({ label: t('facility:deleteFacility'), onClick: () => setShowDeleteModal(true) });
        }
      }

      setMenuButtonItems(menuItems);

      const initialFormValues = {
        ...formValues,
        name: facility.name ?? '',
        address_line1: facility.address_line1,
        address_line2: facility.address_line2 ?? '',
        address_city: facility.address_city,
        address_region: facility.address_region,
        address_post_code: facility.address_post_code,
        utility_id: facility.utility?.id,
        last_active_reporting_quarter: facility.last_active_reporting_quarter,
        comments: facility.comments,
      };

      setFormValues(initialFormValues);
      setOriginalFormValues(initialFormValues);
      setHasChanges(false);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchFacility.error);
    }
  };

  const validateForm = () => {
    const formFields: FormMetadata = {
      name: { label: 'Name', max: 50 },
      address_line1: { label: 'Address', max: 100, required: true },
      address_line2: { label: 'Address', max: 100 },
      address_city: { label: 'City', max: 50, required: true },
      address_post_code: { label: 'Postal code', max: 20, required: true },
      utility_id: { label: 'Utility', required: !isCanada },
      comments: { label: 'Comments', max: 500 },
    };

    const errors = getFormErrors(formFields, formValues);
    if (errors) setFormErrors(errors);
    return !errors;
  };

  const updateFacility = async () => {
    if (!facilityId || !clientId || !validateForm() || !hasChanges) return;

    try {
      setIsUpdating(true);
      if (isFinalized) {
        await FacilityService.updateFinalized({
          id: facilityId,
          name: formValues.name || null,
          utility_id: formValues.utility_id || null,
          comments: formValues.comments || null,
        });
        setOriginalFormValues({ ...formValues });
      } else {
        await FacilityService.updateUnfinalized({
          id: facilityId,
          name: formValues.name || null,
          address_line1: formValues.address_line1,
          address_line2: formValues.address_line2 || null,
          address_city: formValues.address_city,
          address_post_code: formValues.address_post_code,
          utility_id: isCanada ? null : formValues.utility_id,
          comments: formValues.comments || null,
        });
        setOriginalFormValues({ ...formValues });
      }
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(toastMessages.updateFacility.success);
      setHasChanges(false);
    } catch (error) {
      const err = error as ApiError;
      const errors = (err.body as { errors?: ApiErrorItem[] })?.errors ?? [];
      const uniqueNameError = errors.find((e) => e.field === 'name' && e.rule === 'unique');

      if (uniqueNameError) {
        setFormErrors((prev) => ({ ...prev, name: toastMessages.updateFacility.error(err) }));
      } else {
        globalStore.handleApiError(err, toastMessages.updateFacility.error);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    clientStore.setReportingPeriodOptions(undefined);
    clientStore.setReportingPeriods(undefined);
  }, []);

  useEffect(() => {
    if (facilityId) {
      setIsLoading(true);
      setFacility(undefined);
      fetchPageData(facilityId, clientId).finally(() => setIsLoading(false));
    }
  }, [facilityId, clientId]);

  useEffect(() => {
    if (facilityId && shouldRefresh) {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      fetchPageData(facilityId, clientId).finally(() => setShouldRefresh(false));
    }
  }, [facilityId, clientId, shouldRefresh]);

  //404 Redirect
  useEffect(() => {
    if (isLoading) return;
    if (facility && facility.id !== facilityId) return;
    setIsInvalidParams(Number.isNaN(clientId) || Number.isNaN(facilityId) || facility?.id === undefined);
  }, [facilityId, facility, clientId, isLoading]);

  /* Product Tour */
  useProductTour({
    tourName: 'facility-details-tour',
    steps: facilityDetailsTour,
    autoStart: true,
    showOnce: true,
  });

  /** Render **/
  return isInvalidParams ? (
    <PageNotFound />
  ) : (
    <FormPage
      title={getFacilityLabel(facility)}
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            { name: 'Clients', link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined },
            { name: facilityClient?.name || '', link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
            { name: t('common:facilities'), link: `/clients/${clientId}/facilities` },
          ]}
          currentPageName={getFacilityLabel(facility)}
        />
      }
      titleBar={titleBar}
      isLoading={isLoading}
      isDetailPage
      hideMenuButton={!isLoading && facility && !menuButtonItems.length}
      menuButtonItems={menuButtonItems}
    >
      <FormCard
        title={t('common:generalInfo')}
        icon={<CircleInformation size="24px" color="brand" />}
        onSubmit={updateFacility}
        saveButtonLabel={t('common:saveChanges')}
        saveButtonDisabled={!hasChanges}
        isLoading={isLoading || isUpdating}
      >
        <Form>
          <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
            <FormCardSection
              title={t('facility:facilityInfo')}
              width={isMobile ? '100%' : '55%'}
              margin={{ bottom: isMobile ? 'medium' : 'none' }}
              data-tour="facility-info"
            >
              <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                <Box width="100%">
                  <Input
                    id="facility_name"
                    label={t('common:name')}
                    value={formValues.name ?? ''}
                    setValue={(value) => updateFormValue('name', value)}
                    error={formErrors['name']}
                    onSubmit={updateFacility}
                    disabled={isRetired}
                  />
                </Box>
                <Box width="100%" direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="first_active_reporting_quarter"
                    label={t('common:firstReportingPeriod')}
                    value={firstReportingPeriod}
                    required
                    disabled
                    flex
                  />
                </Box>
              </Box>
              <AddressAutocompleteInput
                name="address_line1"
                label={t('common:address')}
                value={formValues.address_line1}
                setValue={(value) => updateFormValue('address_line1', value)}
                onPlaceSelected={handlePlaceSelected}
                error={formErrors['address_line1']}
                onSubmit={updateFacility}
                required
                disabled={isFinalized || isRetired}
              />
              <Input
                id="address_line2"
                autoComplete="address_line2"
                label={t('common:addressContinued')}
                value={formValues.address_line2 ?? ''}
                setValue={(value) => updateFormValue('address_line2', value)}
                error={formErrors['address_line2']}
                onSubmit={updateFacility}
                disabled={isFinalized || isRetired}
              />
              <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                <Input
                  id="address_city"
                  label={t('common:city')}
                  value={formValues.address_city}
                  setValue={(value) => updateFormValue('address_city', value)}
                  error={formErrors['address_city']}
                  onSubmit={updateFacility}
                  required
                  disabled={isFinalized || isRetired}
                  flex
                />
                <Input
                  id="address_region.short_code"
                  label={t('common:stateProvince')}
                  value={formValues.address_region?.short_code ?? ''}
                  required
                  disabled
                  flex
                />
                <Input
                  id="address_post_code"
                  label={t('common:postalCode')}
                  value={formValues.address_post_code}
                  setValue={(value) => updateFormValue('address_post_code', value)}
                  error={formErrors['address_post_code']}
                  onSubmit={updateFacility}
                  required
                  disabled={isFinalized || isRetired}
                  flex
                />
              </Box>
              <Line margin="0.5rem" />
              <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                <Input
                  id="program_id"
                  label={t('common:program')}
                  value={facility?.program?.name}
                  required
                  disabled
                  fill="horizontal"
                />
                {!isCanada && (
                  <UtilitySelect
                    id="utility"
                    label={t('facility:utility')}
                    placeholder={t('facility:chooseUtility')}
                    emptySearchMessage={
                      !formValues.address_region?.id ? t('facility:chooseUtilityHelp') : t('facility:noUtilities')
                    }
                    value={formValues.utility_id}
                    setValue={(value) => updateFormValue('utility_id', value)}
                    error={formErrors['utility_id']}
                    regionId={formValues.address_region?.id}
                    required={!isCanada}
                    disabled={isRetired}
                    fill="horizontal"
                  />
                )}
              </Box>
              <Line margin="0.5rem" />
              {isRetired && (
                <Input
                  name="facility-retirement_reason"
                  label={t('common:reasonForRetiring')}
                  value={facility?.retirement_reason_other || facility?.retirement_reason?.name || ''}
                  disabled
                  placeholder="-"
                  componentType="textArea"
                  opacity={1}
                />
              )}
              <Box flex="grow">
                <Input
                  id="comments"
                  label={t('common:comments')}
                  value={formValues.comments ?? ''}
                  setValue={(value) => updateFormValue('comments', value)}
                  error={formErrors['comments']}
                  onSubmit={updateFacility}
                  componentType="textArea"
                  centerLabel={false}
                  height="12rem"
                  disabled={!userStore.isAdminUser}
                />
              </Box>
            </FormCardSection>
            {!isMobile ? <Line direction="vertical" margin="3rem" /> : <Line margin="small" />}
            <FormCardSection
              width={isMobile ? '100%' : '40%'}
              pad={isMobile ? { top: 'medium', bottom: '75px' } : undefined}
            >
              <ContactsList contacts={facilityUsers} isFacility isUpdatePage />
            </FormCardSection>
          </Box>
        </Form>
      </FormCard>
      <Anchor id="equipment" />
      <Box data-tour="facility-equipment-list">
        <EquipmentList
          id="equipment_list"
          title={t('facility:assetList')}
          clientId={clientId}
          facilityId={facilityId}
          isCanada={isCanada}
          firstReportingQuarter={facility?.first_active_reporting_quarter}
          lastReportingQuarter={facility?.last_active_reporting_quarter}
          showBulkUpdateButton
          showAddButton
          showShareButton
          showSearchInput
        />
      </Box>
      {showRetireModal && !!facility && (
        <RetireFacilityModal
          facility={facility}
          setIsVisible={setShowRetireModal}
          setShouldRefresh={setShouldRefresh}
        />
      )}
      {showDeleteModal && !!facility && (
        <DeleteFacilityModal
          facility={facility}
          setIsVisible={setShowDeleteModal}
          setShouldRefresh={setShouldRefresh}
        />
      )}
    </FormPage>
  );
});

export default FacilityDetailsPage;
